import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { shareReplay, switchMap } from 'rxjs/operators';
import { ReferenceData, ReferenceDataType, HierarchyFamily } from '../models/reference-data.model';
import { API_CONFIG } from '../../../core/config/api.config';

@Injectable({
  providedIn: 'root'
})
export class ReferenceDataService {
  private readonly apiUrl = `${API_CONFIG.referenceData}/ref-data`;
  private readonly cache = new Map<string, Observable<ReferenceData[]>>();

  /**
   * Types servis par partners-service et non par le service reference-data.
   *
   * Les deux services exposent /ref-data mais sur des bases DISTINCTES
   * (dev_partners vs dev_refdata). Un type déclaré dans l'un est inconnu de
   * l'autre, qui répond alors 400 « Invalid reference type ».
   *
   * - petty-cash-reasons : cash_movements.reason_id le référence par clé
   *   étrangère, ce qui impose dev_partners.
   * - brands : la table n'existe que dans dev_partners. Sans ce routage, la
   *   catégorie « Marques » ne chargeait rien du tout.
   */
  private static readonly PARTNERS_OWNED = new Set<ReferenceDataType>([
    'petty-cash-reasons',
    'brands',
  ]);

  private baseFor(type: ReferenceDataType): string {
    return ReferenceDataService.PARTNERS_OWNED.has(type)
      ? `${API_CONFIG.partnersService}/ref-data`
      : this.apiUrl;
  }

  constructor(private http: HttpClient) { }

  getAll(type: ReferenceDataType, activeOnly = false): Observable<ReferenceData[]> {
    if (activeOnly) {
      return this.http.get<ReferenceData[]>(`${this.baseFor(type)}/${type}?activeOnly=true`);
    }
    if (!this.cache.has(type)) {
      this.cache.set(type, this.http.get<ReferenceData[]>(`${this.baseFor(type)}/${type}`).pipe(shareReplay(1)));
    }
    return this.cache.get(type)!;
  }

  invalidateCache(type?: ReferenceDataType) {
    type ? this.cache.delete(type) : this.cache.clear();
  }

  getProductHierarchy(): Observable<HierarchyFamily[]> {
    return this.http.get<HierarchyFamily[]>(`${this.apiUrl}/product-hierarchy`);
  }

  getById(type: ReferenceDataType, id: number): Observable<ReferenceData> {
    return this.http.get<ReferenceData>(`${this.baseFor(type)}/${type}/${id}`);
  }

  create(type: ReferenceDataType, item: Omit<ReferenceData, 'id'>): Observable<ReferenceData> {
    return this.http.post<ReferenceData>(`${this.baseFor(type)}/${type}`, item);
  }

  update(type: ReferenceDataType, id: number, updates: Partial<ReferenceData>): Observable<ReferenceData> {
    return this.http.put<ReferenceData>(`${this.baseFor(type)}/${type}/${id}`, updates);
  }

  delete(type: ReferenceDataType, id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseFor(type)}/${type}/${id}`);
  }

  toggleActive(type: ReferenceDataType, id: number): Observable<ReferenceData> {
    // Since backend doesn't have a specific toggle endpoint, we'll fetch, toggle, and update
    // Or we can just send a partial update with the negated value.
    // But we don't know the current value without fetching or passing it in.
    // Ideally the component should handle the logic and call update().
    // For now, let's implement it by fetching first (inefficient but safe) or assuming the component passes the new state.
    // Actually, looking at the previous implementation:
    /*
      toggleActive(type: ReferenceDataType, id: number): Observable<ReferenceData> {
          this.ensureTypeExists(type);
          const index = this.data[type].findIndex(item => item.id === id);
          if (index !== -1) {
          this.data[type][index].isActive = !this.data[type][index].isActive;
          return of(this.data[type][index]).pipe(delay(300));
          }
          throw new Error(`Item with id ${id} not found`);
      }
    */
    // I will change the signature to expect the new item state from component, or fetch-update.
    // To keep signature compatible for now (though I'm refactoring), I will defer to update.
    // But wait, the component calls this. I should check how it's called.
    // If I change signature, I break components.
    // Let's create a specialized endpoint in backend or logic here.
    // For now, I will use getById then update.
    return this.getById(type, id).pipe(
      switchMap(item => {
        return this.update(type, id, { isActive: !item.isActive });
      })
    );
  }
}
